// src/components/TimeCapsuleWrite.js
import React, { useState } from 'react';
import { apiFetch } from '../utils/apiFetch';
import '../styles/TimeCapsuleWrite.css';
import { useToast } from './Toast';

const TimeCapsuleWrite = ({ diary, onClose, onSuccess }) => {
    const toast = useToast();
    const [formData, setFormData] = useState({
        content: '' // ✅ openDate 필드 제거
    });
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.content.trim()) {
            newErrors.content = '내용을 입력해주세요.';
        } else if (formData.content.length > 2000) {
            newErrors.content = '내용은 2000자 이하로 입력해주세요.';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setIsLoading(true);
        
        try {
            const response = await apiFetch('/timecapsule/write', {
                method: 'POST',
                body: JSON.stringify({
                    diaryId: diary.diaryId,
                    content: formData.content
                    // ✅ openDate 제거
                })
            });
            
            if (response.ok) {
                const result = await response.json();
                
                if (onSuccess) {
                    onSuccess(result);
                }
                
                onClose();
            } else {
                const errorData = await response.json();
                toast.error(errorData.errorMessage || errorData.message || '타임캡슐 작성에 실패했습니다.');
            }
        } catch (error) {
            console.error('타임캡슐 작성 오류:', error);
            toast.error('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleModalClick = (e) => {
        if (e.target.classList.contains('timecapsule-write-overlay')) {
            onClose();
        }
    };

    // 날짜 포맷팅 함수
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const [year, month, day] = dateString.split('-');
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'short'
        });
    };

    return (
        <div className="timecapsule-write-overlay" onClick={handleModalClick}>
            <div className="timecapsule-write-modal">
                <div className="modal-header">
                    <h2>타임캡슐 작성</h2>
                    <button onClick={onClose} className="modal-close-btn">✕</button>
                </div>

                <div className="modal-content">

                    {/* 연결된 일기 정보 표시 */}
                    <div className="diary-reference">
                        <h4>📖 연결된 일기</h4>
                        <div className="diary-reference-content">
                            <p className="diary-title">"{diary.title}"</p>
                            <p className="diary-date">{formatDate(diary.date)}</p>
                        </div>
                    </div>

                    <div className="timecapsule-intro">
                        <p>이 일기에 연결된 특별한 메시지를 작성해보세요! 📝</p>
                    </div>

                    <form onSubmit={handleSubmit} className="timecapsule-form">
                        {/* ✅ 개봉 날짜 입력 섹션 완전 제거 */}
                        
                        <div className="form-group">
                            <label htmlFor="content" className="form-label">
                                비밀 메시지 * 💝
                            </label>
                            <textarea
                                id="content"
                                name="content"
                                value={formData.content}
                                onChange={handleInputChange}
                                className={`form-textarea ${errors.content ? 'error' : ''}`}
                                placeholder="이 일기와 함께 미래에 전하고 싶은 메시지를 작성해보세요!"
                                rows={12}
                                maxLength={2000}
                                autoFocus
                            />
                            {errors.content && (
                                <span className="error-text">{errors.content}</span>
                            )}
                            <small className="form-hint">
                                {formData.content.length}/2000자
                            </small>
                        </div>

                        <div className="form-preview">
                            <h3>📦 타임캡슐 미리보기</h3>
                            <div className="preview-card">
                                <div className="preview-header">
                                    <h4>💌 "{diary.title}"에 대한 비밀 메시지</h4>
                                    <div className="preview-info">
                                        <p>🎯 개봉일: {diary.openDate ? formatDate(diary.openDate) : '일기에서 설정한 개봉일'}</p>
                                    </div>
                                </div>
                                <div className="preview-content">
                                    {formData.content || '메시지를 입력하세요...'}
                                </div>
                            </div>
                        </div>

                        <div className="form-actions">
                            <button 
                                type="button" 
                                className="cancel-btn"
                                onClick={onClose}
                            >
                                취소
                            </button>
                            <button 
                                type="submit" 
                                className="submit-btn"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <div className="button-spinner"></div>
                                        타임캡슐 생성 중...
                                    </>
                                ) : (
                                    '타임캡슐 만들기 🎁'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default TimeCapsuleWrite;
